/**
 * Shared logic for evaluating conditional/dependsOn visibility of form fields.
 * Used by GenerateSchemaForm (top-level) and handleFieldType (nested object/group fields).
 */

export function evaluateFormCondition(
  fieldSchema: any,
  formValues: any,
  allSchemaFields: any[] = []
): boolean {
  const fieldName = fieldSchema.title || fieldSchema.key;

  // Backend format: dependsOn or validation.dependsOn
  if (fieldSchema.dependsOn || fieldSchema.validation?.dependsOn) {
    const dependsOnConfig = fieldSchema.dependsOn || fieldSchema.validation?.dependsOn;
    const { field: dependentFieldName, value: expectedValue } = dependsOnConfig;

    let actualValue = formValues[dependentFieldName];
    if (actualValue === undefined && allSchemaFields.length > 0) {
      const dependentField = allSchemaFields.find(
        (f) =>
          (f.title || f.key) === dependentFieldName ||
          f.title === dependentFieldName ||
          f.key === dependentFieldName
      );
      if (dependentField) {
        actualValue =
          formValues[dependentField.key] ??
          formValues[dependentField.title] ??
          formValues[dependentFieldName];
      }
    }

    const fieldExistsInSchema = allSchemaFields.some(
      (field) =>
        (field.title || field.key) === dependentFieldName ||
        field.title === dependentFieldName ||
        field.key === dependentFieldName
    );

    if (actualValue === undefined && !fieldExistsInSchema && allSchemaFields.length > 0) {
      const fieldMappings: Record<string, string[]> = {
        is_business: ['business', 'organisation', 'organisation', 'company'],
        business: ['business', 'organisation', 'organisation', 'company'],
        industry: ['industry'],
        company_size: ['company', 'size', 'employees'],
        decision_maker: ['decision', 'maker'],
        budget_range: ['budget', 'range'],
      };
      const matchingField = allSchemaFields.find((field) => {
        const fieldKey = (field.title || field.key || '').toLowerCase();
        const dependentLower = String(dependentFieldName).toLowerCase();
        const mappingKeys = fieldMappings[dependentLower] || [dependentLower];
        return mappingKeys.some((key) => fieldKey.includes(key));
      });
      if (matchingField) {
        actualValue = formValues[matchingField.title || matchingField.key];
      }
    }

    if (actualValue === undefined || actualValue === null) {
      const dependentField = allSchemaFields.find((f) => (f.title || f.key) === dependentFieldName);
      if (dependentField?.type === 'boolean') {
        return expectedValue === 'false' || expectedValue === false;
      }
      return false;
    }

    if (Array.isArray(expectedValue)) {
      return expectedValue.includes(actualValue);
    }
    if (typeof expectedValue === 'string' && (expectedValue === 'true' || expectedValue === 'false')) {
      return String(actualValue).toLowerCase() === expectedValue;
    }
    if (typeof expectedValue === 'boolean') {
      return (actualValue === true || actualValue === 'true') === expectedValue;
    }
    return actualValue === expectedValue;
  }

  // Frontend format: conditional with operator
  if (fieldSchema.conditional) {
    const { field, operator, value } = fieldSchema.conditional;
    let fieldValue = formValues[field];

    if (fieldValue === undefined && allSchemaFields.length > 0) {
      const conditionalField = allSchemaFields.find(
        (f) =>
          (f.title || f.key) === field ||
          f.title === field ||
          f.key === field
      );
      if (conditionalField) {
        fieldValue =
          formValues[conditionalField.key] ??
          formValues[conditionalField.title] ??
          formValues[field];
      }
    }

    switch (operator) {
      case 'equals':
        return fieldValue === value;
      case 'not_equals':
        return fieldValue !== value;
      case 'contains':
        return Array.isArray(fieldValue)
          ? fieldValue.includes(value)
          : String(fieldValue).includes(value);
      case 'not_contains':
        return Array.isArray(fieldValue)
          ? !fieldValue.includes(value)
          : !String(fieldValue).includes(value);
      case 'greater_than':
        return Number(fieldValue) > Number(value);
      case 'less_than':
        return Number(fieldValue) < Number(value);
      case 'is_empty':
        return (
          !fieldValue ||
          fieldValue === '' ||
          (Array.isArray(fieldValue) && fieldValue.length === 0)
        );
      case 'is_not_empty':
        return (
          !!fieldValue &&
          fieldValue !== '' &&
          (!Array.isArray(fieldValue) || fieldValue.length > 0)
        );
      default:
        return true;
    }
  }

  return true;
}

/** Build scoped form values for nested object fields (parent.child → child) */
export function getNestedFormValues(
  fullFormValues: any,
  baseFieldName: string
): Record<string, any> {
  if (!fullFormValues) return {};
  const scoped: Record<string, any> = {};
  const prefixes = [`${baseFieldName}.`, `${baseFieldName.toLowerCase()}.`];
  for (const key of Object.keys(fullFormValues)) {
    for (const prefix of prefixes) {
      if (key.startsWith(prefix)) {
        scoped[key.slice(prefix.length)] = fullFormValues[key];
        break;
      }
    }
  }
  const nested = fullFormValues[baseFieldName] ?? fullFormValues[baseFieldName.toLowerCase()];
  if (typeof nested === 'object' && nested !== null && !Array.isArray(nested)) {
    Object.assign(scoped, nested);
  }
  return scoped;
}
